import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Fonts
import "./assets/fonts/SegoeUIBoldItalic.ttf";
import "./assets/fonts/SegoeUIBold.ttf";
import "./assets/fonts/SegoeUIItalic.ttf";
import "./assets/fonts/SegoeUI.ttf";

import { PAGE_PATHS } from "./pages/page-paths";
import usePromise from "./utils/hooks/usePromise";
import { resolveServices } from "./services";
import VLEStatsPage from "./pages/VLEStats";
import CountryStatsPage from "./pages/CountryStats";
import Loader from "./assets/animated-images/loader";
import { useAuth } from "./utils/hooks/useAuth";
import LoginPage from "./pages/Login";
import { ADMIN_ROLE, AdminRole } from "./utils/roles";
import PlacementPartnersPage from "./pages/PlacementPartners";
import LearnerPage from "./pages/Learner";
import AdminManagementPage from "./pages/AdminManagement";

type AppRoute = {
  path: string;
  element: JSX.Element;
  roles: AdminRole[];
};

function App() {
  const { result: servicesResolved } = usePromise(
    { fn: resolveServices, runOnMount: { args: [] } },
    []
  );

  const { is_authenticated, role } = useAuth();

  if (servicesResolved) {
    const learnerAccessRoles = [
      ADMIN_ROLE.SUPER_ADMIN,
      ADMIN_ROLE.CENTRAL_ADMIN,
      ADMIN_ROLE.ADMIN,
      ADMIN_ROLE.STATE_ADMIN,
      ADMIN_ROLE.PP_ADMIN,
    ];
    const placementPartnerRoles = [
      ADMIN_ROLE.SUPER_ADMIN,
      ADMIN_ROLE.CENTRAL_ADMIN,
      ADMIN_ROLE.ADMIN,
    ];

    const appRoutes: AppRoute[] = [
      {
        path: PAGE_PATHS.VLE_STATS,
        element: <VLEStatsPage />,
        roles: learnerAccessRoles,
      },
      {
        path: PAGE_PATHS.COUNTRY_STATS,
        element: <CountryStatsPage />,
        roles: learnerAccessRoles,
      },
      {
        path: PAGE_PATHS.LEARNER,
        element: <LearnerPage />,
        roles: learnerAccessRoles,
      },
      {
        path: PAGE_PATHS.LEARNER_REPORTS,
        element: <Navigate to={PAGE_PATHS.LEARNER} replace />,
        roles: learnerAccessRoles,
      },
      {
        path: PAGE_PATHS.PLACEMENT_PARTNERS,
        element: <PlacementPartnersPage />,
        roles: placementPartnerRoles,
      },
      {
        path: PAGE_PATHS.ADMINS,
        element: <AdminManagementPage />,
        roles: [ADMIN_ROLE.SUPER_ADMIN],
      },
      {
        path: PAGE_PATHS.UPLOADS,
        element: <Navigate to={PAGE_PATHS.LEARNER} replace />,
        roles: [ADMIN_ROLE.SUPER_ADMIN, ADMIN_ROLE.CENTRAL_ADMIN],
      },
      {
        path: PAGE_PATHS.UPDATE_USER_COURSE_COMPLETION,
        element: <Navigate to={PAGE_PATHS.LEARNER} replace />,
        roles: [ADMIN_ROLE.SUPER_ADMIN, ADMIN_ROLE.CENTRAL_ADMIN],
      },
      {
        path: PAGE_PATHS.UPDATE_STATE_LIMIT,
        element: <Navigate to={PAGE_PATHS.VLE_STATS} replace />,
        roles: [ADMIN_ROLE.SUPER_ADMIN],
      },
      {
        path: PAGE_PATHS.DEFAULT,
        element: <Navigate to={PAGE_PATHS.COUNTRY_STATS} replace />,
        roles: learnerAccessRoles,
      },
    ];

    return (
      <BrowserRouter>
        <Routes>
          {is_authenticated ? (
            <>
              {appRoutes.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    role && route.roles.includes(role) ? (
                      route.element
                    ) : (
                      <Navigate to={PAGE_PATHS.DEFAULT} replace />
                    )
                  }
                />
              ))}
              <Route
                path="*"
                element={<Navigate to={PAGE_PATHS.DEFAULT} replace />}
              />
            </>
          ) : (
            <>
              <Route path={PAGE_PATHS.DEFAULT} element={<LoginPage />} />
              <Route
                path="*"
                element={<Navigate to={PAGE_PATHS.DEFAULT} replace />}
              />
            </>
          )}
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </BrowserRouter>
    );
  } else
    return (
      <div className="centered-page">
        <Loader style={{ height: "40px" }} color="#999" />
      </div>
    );
}

export default App;
